import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const AdminLoginPage: React.FC = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [requiresTotp, setRequiresTotp] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const [showSetupAuth, setShowSetupAuth] = useState(false);
  const [setupEmail, setSetupEmail] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupMessage, setSetupMessage] = useState('');
  const [setupError, setSetupError] = useState('');

  const { login, verifyTotpLogin } = useAuth();
  const navigate = useNavigate();

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await login(formData.email, formData.password);
      if (response?.requiresTotpSetup) {
        setShowSetupAuth(true);
        setSetupEmail(formData.email);
        setSetupPassword(formData.password);
        setSetupMessage('يجب إعداد وسيلة التأكيد أولاً. قم بالضغط على "إظهار QR" ثم أدخل الرمز للمتابعة.');
        return;
      }
      if (response?.requiresTotp && response?.challengeToken) {
        setChallengeToken(response.challengeToken);
        setRequiresTotp(true);
        return;
      }
      navigate('/admin');
    } catch (err: any) {
      setError(err?.message || 'حدث خطأ أثناء تسجيل الدخول');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await verifyTotpLogin(challengeToken, totpCode);
      navigate('/admin');
    } catch (err: any) {
      setError(err?.message || 'رمز التحقق غير صحيح');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupInit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSetupLoading(true);
    setSetupError('');
    setSetupMessage('');
    try {
      const { data } = await api.post('/api/auth/totp/setup-init', {
        email: setupEmail,
        password: setupPassword
      });
      setSetupToken(data.setupToken);
      setQrCodeDataUrl(data.qrCodeDataUrl);
      setManualKey(data.manualKey);
    } catch (err: any) {
      setSetupError(err?.response?.data?.error || 'فشل بدء إعداد المصادقة');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSetupComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    setSetupLoading(true);
    setSetupError('');
    setSetupMessage('');
    try {
      await api.post('/api/auth/totp/setup-complete', {
        setupToken,
        code: setupCode
      });
      setSetupMessage('تم ربط وسيلة التأكيد بنجاح. يمكنك الآن تسجيل الدخول باستخدام الرمز.');
      setSetupCode('');
      setSetupToken('');
      setQrCodeDataUrl('');
      setManualKey('');
      setSetupEmail('');
      setSetupPassword('');
    } catch (err: any) {
      setSetupError(err?.response?.data?.error || 'فشل تأكيد إعداد المصادقة');
    } finally {
      setSetupLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-pink-50 to-fuchsia-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">Twine Candles</h1>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-dark-text">دخول الإدارة</h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-dark-textSecondary">للوصول إلى لوحة التحكم</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white/95 dark:bg-dark-surface/95 py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border border-rose-100 dark:border-dark-border transition-colors duration-300" dir="rtl">
          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {!requiresTotp ? (
            <form className="space-y-6" onSubmit={handleLoginSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary">
                  البريد الإلكتروني
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md placeholder-gray-400 dark:placeholder-dark-textSecondary focus:outline-none focus:ring-pink-400 focus:border-pink-400 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text transition-colors duration-300"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary">
                  كلمة المرور
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md placeholder-gray-400 dark:placeholder-dark-textSecondary focus:outline-none focus:ring-pink-400 focus:border-pink-400 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text transition-colors duration-300"
                    placeholder="كلمة المرور"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-2.5 text-gray-500 dark:text-dark-textSecondary hover:text-gray-800 dark:hover:text-dark-text transition-colors"
                  >
                    {showPassword ? 'إخفاء' : 'إظهار'}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowSetupAuth(!showSetupAuth);
                    setSetupError('');
                    setSetupMessage('');
                  }}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-dark-border text-sm font-medium rounded-md text-gray-700 dark:text-dark-text bg-gray-100 dark:bg-dark-hover hover:bg-gray-200 dark:hover:bg-dark-border transition-colors"
                >
                  ربط وسيلة التأكيد
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleTotpSubmit}>
              <div>
                <label htmlFor="totpCode" className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary">
                  رمز التأكيد
                </label>
                <div className="mt-1">
                  <input
                    id="totpCode"
                    name="totpCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md placeholder-gray-400 dark:placeholder-dark-textSecondary focus:outline-none focus:ring-pink-400 focus:border-pink-400 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text tracking-widest text-center transition-colors duration-300"
                    placeholder="رمز التأكيد"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {loading ? 'جاري التحقق...' : 'تأكيد الرمز'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRequiresTotp(false);
                    setChallengeToken('');
                    setTotpCode('');
                    setError('');
                  }}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-dark-border text-sm font-medium rounded-md text-gray-700 dark:text-dark-text bg-gray-100 dark:bg-dark-hover hover:bg-gray-200 dark:hover:bg-dark-border transition-colors"
                >
                  رجوع
                </button>
              </div>
            </form>
          )}

          {showSetupAuth && !requiresTotp && (
            <div className="mt-6 border-t border-gray-200 dark:border-dark-border pt-6 space-y-4">
              <h3 className="text-gray-900 dark:text-dark-text font-semibold">ربط وسيلة التأكيد</h3>

              {setupError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-md text-sm">
                  {setupError}
                </div>
              )}
              {setupMessage && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-3 py-2 rounded-md text-sm">
                  {setupMessage}
                </div>
              )}

              {!setupToken ? (
                <form onSubmit={handleSetupInit} className="space-y-3">
                  <input
                    type="email"
                    placeholder="البريد الإلكتروني"
                    required
                    value={setupEmail}
                    onChange={(e) => setSetupEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md placeholder-gray-400 dark:placeholder-dark-textSecondary focus:outline-none focus:ring-pink-400 focus:border-pink-400 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text transition-colors duration-300"
                  />
                  <input
                    type="password"
                    placeholder="كلمة المرور"
                    required
                    value={setupPassword}
                    onChange={(e) => setSetupPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md placeholder-gray-400 dark:placeholder-dark-textSecondary focus:outline-none focus:ring-pink-400 focus:border-pink-400 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text transition-colors duration-300"
                  />
                  <button
                    type="submit"
                    disabled={setupLoading}
                    className="w-full py-2 rounded-md text-white bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 disabled:opacity-50 transition-colors"
                  >
                    {setupLoading ? 'جاري التحضير...' : 'إظهار QR'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSetupComplete} className="space-y-3">
                  <div className="bg-gray-50 dark:bg-dark-hover p-3 rounded-md flex justify-center border border-gray-200 dark:border-dark-border">
                    <img src={qrCodeDataUrl} alt="رمز الاستجابة السريعة" className="w-48 h-48" />
                  </div>
                  <div className="text-xs text-gray-600 dark:text-dark-textSecondary break-all">
                    المفتاح اليدوي: <span className="text-gray-900 dark:text-dark-text">{manualKey}</span>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="أدخل رمز التطبيق"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md placeholder-gray-400 dark:placeholder-dark-textSecondary focus:outline-none focus:ring-pink-400 focus:border-pink-400 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text tracking-widest text-center transition-colors duration-300"
                  />
                  <button
                    type="submit"
                    disabled={setupLoading}
                    className="w-full py-2 rounded-md text-white bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 disabled:opacity-50 transition-colors"
                  >
                    {setupLoading ? 'جاري التأكيد...' : 'تأكيد الربط'}
                  </button>
                </form>
              )}
            </div>
          )}

          <div className="text-center mt-6">
            <Link to="/" className="text-sm text-pink-600 hover:text-pink-700 dark:text-pink-400 dark:hover:text-pink-300 transition-colors">
              العودة إلى الموقع الرئيسي
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
